/**
 * Forward browser warnings/errors to the server session log.
 */
import type { LogLevel } from "@memes/shared";
import { devHeaders } from "./devKey";

const CLIENT_ID_KEY = "memes-client-log-id";
const FLUSH_MS = 2000;

interface QueuedEntry {
  ts: string;
  level: LogLevel;
  msg: string;
  module?: string;
  url?: string;
  [key: string]: unknown;
}

const queue: QueuedEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let clientId = "";
let installed = false;

function getClientId(): string {
  if (clientId) return clientId;
  try {
    const existing = sessionStorage.getItem(CLIENT_ID_KEY);
    if (existing) {
      clientId = existing;
      return clientId;
    }
    clientId = crypto.randomUUID();
    sessionStorage.setItem(CLIENT_ID_KEY, clientId);
    return clientId;
  } catch {
    clientId = crypto.randomUUID();
    return clientId;
  }
}

function enqueue(
  level: LogLevel,
  module: string,
  msg: string,
  extra?: Record<string, unknown>,
): void {
  queue.push({
    ts: new Date().toISOString(),
    level,
    msg,
    module,
    url: typeof window !== "undefined" ? window.location.pathname : undefined,
    ...extra,
  });
  if (queue.length > 200) queue.splice(0, queue.length - 200);
}

async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const entries = queue.splice(0, queue.length);
  try {
    const res = await fetch("/api/v1/dev/logs/client", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...devHeaders() },
      body: JSON.stringify({ clientId: getClientId(), entries }),
      keepalive: true,
    });
    if (!res.ok && res.status !== 403) {
      /* re-queue on transient failure */
      queue.unshift(...entries);
    }
  } catch {
    queue.unshift(...entries);
  }
}

export const clientLog = {
  warn(module: string, msg: string, extra?: Record<string, unknown>): void {
    enqueue("warn", module, msg, extra);
    console.warn(`[${module}]`, msg, extra ?? "");
  },
  error(module: string, msg: string, extra?: Record<string, unknown>): void {
    enqueue("error", module, msg, extra);
    console.error(`[${module}]`, msg, extra ?? "");
  },
  info(module: string, msg: string, extra?: Record<string, unknown>): void {
    enqueue("info", module, msg, extra);
  },
  debug(module: string, msg: string, extra?: Record<string, unknown>): void {
    enqueue("debug", module, msg, extra);
  },
};

export function initClientLogger(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  getClientId();

  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    origError(...args);
    const msg = args.map(String).join(" ");
    if (!msg.includes("[client]")) {
      enqueue("error", "console", msg);
    }
  };

  console.warn = (...args: unknown[]) => {
    origWarn(...args);
    const msg = args.map(String).join(" ");
    if (!msg.includes("[client]")) {
      enqueue("warn", "console", msg);
    }
  };

  flushTimer = setInterval(() => void flush(), FLUSH_MS);
  window.addEventListener("beforeunload", () => void flush());
  window.addEventListener("pagehide", () => void flush());
}
